import { formatZi } from '@/domains/tickets/utils/tier-state';
import { formateazaFel } from './stare-recipiente';
import type { FiltruRaportRecipiente, WasteContainerRequestReportDto } from '../api/waste-container-requests.api';

/**
 * Randurile listei de lucru a adminului (SP-04, partea a doua) — partea PURA.
 *
 * 🔴 Ecranul NU e un raport obisnuit, e o LISTA DE LUCRU: se **goleste**, nu doar se
 * citeste. Ordinea implicita (cea mai veche cerere sus), filtrarea pe stare/fel,
 * cautarea, sortarea, contoarele chip-urilor si exportul le face BACKENDUL, din
 * 29.09 (raport paginat + `/report/contoare`). Aici raman doar etichetele de pe
 * ecran, pure tocmai ca sa existe o poarta automata: componentele nu se pot monta
 * (`environment: 'node'`, fara jsdom).
 */

/**
 * Cele TREI stari ale unui rand, direct din `stare`: NOUA -> `in-asteptare`,
 * PROCESATA -> `confirmata`, LIVRATA -> `livrata` (SE-18).
 *
 * 🔴 Pana pe 29.09 exista si a patra, `fara-anunt` (PROCESATA fara `trimisLaDt`,
 * adica emailul catre logistica n-a plecat). Logistica nu mai e notificata prin
 * email — vede cererile confirmate direct in aplicatie — deci `trimisLaDt` nu mai
 * decide nimic, si alarma ar fi semnalat o pierdere care nu mai poate exista.
 * Filtrul trimis backendului e acelasi, cu numele enumului lui (`IN_ASTEPTARE`,
 * `CONFIRMATA`, `LIVRATA` — `StareRandRecipiente.java`), vezi `FiltruStare`.
 *
 * 🔴 `trimis` a devenit `confirmata` (SE-18, owner 2026-09-16): eticheta „Trimis"
 * era gresita dupa propriul javadoc al enumului — `PROCESATA` inseamna doar «un
 * om se ocupa», nu «a plecat un colet». Acum ca exista `LIVRATA`, o stare REALA de
 * livrare, «Trimis» pentru PROCESATA ar fi concurat cu «Livrată» pentru acelasi sens.
 */
export type StareRand = 'in-asteptare' | 'confirmata' | 'livrata';

export function stareRand(rand: WasteContainerRequestReportDto | undefined | null): StareRand {
    if (!rand) return 'in-asteptare';
    if (rand.stare === 'LIVRATA') return 'livrata';
    if (rand.stare === 'PROCESATA') return 'confirmata';
    return 'in-asteptare';
}

/** Eticheta de pe ecran, cu data doar cand serverul o da. */
export function etichetaStare(rand: WasteContainerRequestReportDto): string {
    const stare = stareRand(rand);
    if (stare === 'in-asteptare') return 'În așteptare';
    if (stare === 'livrata') {
        const zi = typeof rand.livrataLaDt === 'string' ? formatZi(rand.livrataLaDt) : null;
        // Data lipsa nu se inventeaza: „Livrată" fara data e adevarat, „Livrată pe azi" nu e.
        return zi ? `Livrată pe ${zi}` : 'Livrată';
    }
    const zi = typeof rand.procesataLaDt === 'string' ? formatZi(rand.procesataLaDt) : null;
    return zi ? `Confirmată pe ${zi}` : 'Confirmată';
}

/** Filtrul pe stare al chip-urilor, cu valorile din spec; `toate` = fara filtru. */
export type FiltruStare = NonNullable<FiltruRaportRecipiente['stareRand']> | 'toate';

/**
 * Filtrul pe felul cerut. O cerere are mereu UN singur fel (backendul face cate o
 * cerere per fel), deci o scoala care a cerut ambele apare pe doua randuri.
 * «Ambele» = fara filtru pe fel (owner, 2026-09-22), nu „scolile cu ambele cereri".
 */
export type FiltruFel = NonNullable<FiltruRaportRecipiente['fel']> | 'ambele';

/** Numele felului, fara cantitate — cantitatea are coloana ei («# recipiente»). */
export function numeFel(rand: WasteContainerRequestReportDto): string | null {
    return rand.fel ? formateazaFel(rand.fel) : null;
}

/**
 * Cate recipiente a cerut randul.
 *
 * Recipientul DBA e o singura bucata: backendul nu-i tine cantitatea (`null`,
 * `WasteContainerRequest.java`), iar owner-ul a decis (2026-09-22) ca pe ecran
 * apare **1**. La minirecipiente, cantitatea ceruta — iar daca lipseste, `null`:
 * nu inventam un numar, aceeasi regula ca `formateazaFel`.
 */
export function numarRecipiente(rand: WasteContainerRequestReportDto): number | null {
    if (rand.fel === 'RECIPIENT_DBA') return 1;
    if (rand.fel !== 'MINIRECIPIENTE') return null;
    const n = rand.cantitate;
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
}

/** Ziua calendaristica „YYYY-MM-DD" a unui moment, in Europe/Bucharest. */
function ziLaBucuresti(moment: Date): string {
    // `en-CA` scrie data exact ca ISO (2026-09-22); fusul orar e al business-ului, nu al browserului.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Bucharest',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(moment);
}

/**
 * De cate zile calendaristice asteapta cererea. `timestamp` e un `LocalDateTime`
 * fara fus (ora Romaniei), deci ii luam doar data; „azi" se calculeaza tot la
 * Bucuresti. `null` fara data — nu inventam o vechime.
 */
export function zileDeLa(timestamp: string | undefined | null, azi: Date): number | null {
    const m = typeof timestamp === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(timestamp) : null;
    if (!m) return null;
    const [ay, am, ad] = ziLaBucuresti(azi).split('-').map(Number);
    const cerere = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const astazi = Date.UTC(ay, am - 1, ad);
    return Math.max(0, Math.round((astazi - cerere) / 86_400_000));
}

/** „azi" / „de o zi" / „de 5 zile" / „de 20 de zile" (de la 20 in sus, romana cere „de"). */
export function etichetaVechime(zile: number | null): string | null {
    if (zile === null) return null;
    if (zile === 0) return 'azi';
    if (zile === 1) return 'de o zi';
    const rest = zile % 100;
    return rest >= 1 && rest <= 19 ? `de ${zile} zile` : `de ${zile} de zile`;
}

/** „Judet · Oras" din adresa de LIVRARE. */
export function localitate(rand: WasteContainerRequestReportDto): string {
    return [rand.deliveryAddress?.judet, rand.deliveryAddress?.oras].filter(Boolean).join(' · ');
}

/** Numele si telefonul persoanei pentru livrare — poate fi alta decat profesorul. */
export function pentruLivrare(rand: WasteContainerRequestReportDto): string {
    return [rand.contactPersonName, rand.contactPersonPhone].filter(Boolean).join(' · ');
}

/** Profesorul care a cerut. Are email, NU telefon. */
export function aCerut(rand: WasteContainerRequestReportDto): string {
    return [rand.solicitantNume, rand.solicitantEmail].filter(Boolean).join(' · ');
}
